import dayjs from 'dayjs';

// Format date in a user-friendly way
export const formatDate = (dateString: string): string => {
    return dayjs(dateString).format('MMM D, YYYY');
};

// Format date with time
export const formatDateTime = (dateString: string, timeString?: string): string => {
    if (timeString) {
        // If we have a separate time string (HH:MM format)
        const [hours, minutes] = timeString.split(':');
        const date = dayjs(dateString).hour(parseInt(hours)).minute(parseInt(minutes));
        return date.format('MMM D, YYYY - h:mm A');
    }
    return dayjs(dateString).format('MMM D, YYYY - h:mm A');
};

// Format date range (e.g., "May 15-17, 2025" or "May 15, 2025")
export const formatDateRange = (startDate: string, endDate: string, isSingleDay?: boolean): string => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    // Trust the backend-computed flag rather than comparing calendar dates, which can
    // disagree with reality once a UTC timestamp is converted to local display time.
    if (isSingleDay) {
        return start.format('MMM D, YYYY');
    }

    if (start.year() !== end.year()) {
        return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
    } else if (start.month() !== end.month()) {
        return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
    } else if (start.date() !== end.date()) {
        return `${start.format('MMM D')}-${end.format('D, YYYY')}`;
    } else {
        return start.format('MMM D, YYYY');
    }
};

const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Output: Friday 22nd - Monday 25th August 2025 (or a single date when the event is one day)
export const formatFullDateRange = (start: string, end: string, isSingleDay?: boolean) => {
  const startDate = dayjs(start);
  const endDate = dayjs(end);

  const startDay = startDate.format("dddd");
  const startDateNumber = startDate.date();
  const startFormatted = `${startDay} ${startDateNumber}${getOrdinal(startDateNumber)}`;

  // Trust an explicit backend flag either way; only fall back to comparing local
  // calendar dates when the backend hasn't supplied the flag at all.
  if (isSingleDay ?? startDate.isSame(endDate, 'day')) {
    return `${startFormatted} ${startDate.format("MMMM YYYY")}`;
  }

  const endDay = endDate.format("dddd");
  const endDateNumber = endDate.date();
  const endFormatted = `${endDay} ${endDateNumber}${getOrdinal(endDateNumber)} ${endDate.format("MMMM YYYY")}`;

  return `${startFormatted} - ${endFormatted}`;
};

// Format time range
export const formatTimeRange = (startTime: string, endTime: string): string => {
    // Display in 24-hour format (HH:mm), trimming any seconds from the raw value
    const formatTime = (time: string) => time.slice(0, 5);

    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};

// Truncate text with ellipsis
export const truncateText = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return '';
  return text.length <= maxLength ? text : text.substring(0, maxLength) + '...';
};

// Collapse an event's location cities to a short summary, e.g. "London +2 more" — dedupes
// repeated venue names (common for multi-session online events) so the string can't blow out a row.
export const formatEventLocationSummary = (event: { locationCity?: string[]; location?: string }): string => {
  const uniqueCities = Array.from(new Set((event.locationCity ?? []).filter(Boolean)));
  if (uniqueCities.length === 0) return event.location ?? '';
  return uniqueCities.length > 1 ? `${uniqueCities[0]} +${uniqueCities.length - 1} more` : uniqueCities[0];
};

// Whether an event's run has finished — used to tag/exclude past events in search & discovery.
export const isEventEnded = (endDate: string): boolean => dayjs(endDate).isBefore(dayjs());


// Calculate if registration is still open (includes the full deadline day)
export const isRegistrationOpen = (deadline: string): boolean => {
    return dayjs(deadline).endOf('day').isAfter(dayjs());
};

// Calculate days remaining until registration deadline (includes the deadline day itself)
export const daysUntilDeadline = (deadline: string): number => {
    return Math.max(0, dayjs(deadline).startOf('day').diff(dayjs().startOf('day'), 'day') + 1);
};

// Get event status text
export const getEventStatusText = (status: number): string => {
    switch (status) {
        case 0: return 'Active';
        case 1: return 'Draft';
        case 2: return 'Cancelled';
        case 3: return 'Completed';
        default: return 'Unknown';
    }
};

// Get event status color
export const getEventStatusColor = (status: number): string => {
    switch (status) {
        case 0: return 'success';
        case 1: return 'info';
        case 2: return 'error';
        case 3: return 'warning';
        default: return 'default';
    }
};